'use client'

import { Cloud, CloudOff, Loader2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSynopticProject } from '@/lib/synoptic-project-provider'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useLanguage } from '@/lib/i18n/language-provider'

interface SaveIndicatorProps {
  className?: string
  showText?: boolean
  size?: 'sm' | 'md' | 'lg'
  onSave?: () => void // Fonction de sauvegarde fournie par le parent
}

export function SaveIndicator({ 
  className,
  showText = true,
  size = 'md',
  onSave
}: SaveIndicatorProps) {
  const { isSaving, isSaved, lastSavedAt, error, currentProject } = useSynopticProject()
  const { t, language } = useLanguage()
  
  const handleSave = () => {
    if (!currentProject || isSaving) return // Ne bloquer que si en cours de sauvegarde
    
    // Déclencher la sauvegarde fournie par le parent
    if (onSave) {
      onSave()
    }
  }
  
  const iconSize = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  }[size]
  
  const textSize = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  }[size]
  
  // État d'erreur
  if (error) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              className={cn(
                'flex items-center gap-2 text-destructive',
                textSize,
                className
              )}
            >
              <CloudOff className={cn(iconSize, 'animate-pulse')} />
              {showText && <span>{t("synoptic.save.errorSaving")}</span>}
            </Button>
          </TooltipTrigger>
           <TooltipContent className="z-[9999] bg-popover border border-border shadow-lg">
             <p className="text-sm">{error}</p>
             <p className="text-xs text-muted-foreground mt-1">
               {t("synoptic.save.errorDesc")}
             </p>
           </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  
  // En cours de sauvegarde
  if (isSaving) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className={cn(
          'flex items-center gap-2 text-muted-foreground',
          textSize,
          className
        )}
      >
        <Loader2 className={cn(iconSize, 'animate-spin')} />
        {showText && <span>{t("synoptic.save.saving")}</span>}
      </Button>
    )
  }
  
   // 🎨 Sauvegardé avec couleurs dynamiques fluo-rétro
   if (isSaved && lastSavedAt) {
     const timeAgo = formatTimeAgo(lastSavedAt, language, t)
     const timeElapsed = Date.now() - lastSavedAt.getTime()
     const locale = language === 'fr' ? 'fr-FR' : 'en-US'
     
     // 🌟 Calcul des couleurs fluo-rétro
     const seconds = timeElapsed / 1000
     let buttonColors = { border: '', bg: '', text: '' }
     
     if (seconds < 30) {
       buttonColors = { border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-700' }
     } else if (seconds < 60) {
       buttonColors = { border: 'border-green-600', bg: 'bg-green-100', text: 'text-green-800' }
     } else if (seconds < 120) {
       buttonColors = { border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700' }
     } else if (seconds < 300) {
       buttonColors = { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' }
     } else if (seconds < 600) {
       buttonColors = { border: 'border-orange-600', bg: 'bg-orange-100', text: 'text-orange-800' }
     } else {
       buttonColors = { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700' }
     }
     
     return (
       <Button
         variant="outline"
         size="sm"
         onClick={handleSave}
         disabled={isSaving}
         className={cn(
           'flex items-center gap-2 transition-all duration-500 ease-in-out hover:scale-[1.02] active:scale-[0.98]',
           buttonColors.border,
           buttonColors.bg,
           buttonColors.text,
           'hover:opacity-90 relative', // ⬆️ Z-index retiré, le gérer via CSS
           textSize,
           className
         )}
       >
         <Check className={cn(iconSize, 'animate-pulse')} />
         {showText && <span>{t("synoptic.save.saved", { time: timeAgo })}</span>}
       </Button>
     )
   }
  
  // Non sauvegardé
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={!currentProject || isSaving}
            className={cn(
              'flex items-center gap-2 text-muted-foreground hover:text-primary',
              textSize,
              className
            )}
          >
            <Cloud className={iconSize} />
            {showText && <span>{t("synoptic.save.notSaved")}</span>}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="z-[9999] bg-popover border border-border shadow-lg">
          <p className="text-sm">{t("synoptic.save.clickToSave")}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t("synoptic.save.autoSave")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Formatte une date en "il y a X"
 */
function formatTimeAgo(date: Date, language: 'fr' | 'en', t: (key: string, variables?: Record<string, string | number>) => string): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  
  if (seconds < 10) return t("time.now")
  if (seconds < 60) return t("time.secondsAgo", { n: seconds })
  
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t("time.minutesAgo", { n: minutes })
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t("time.hoursAgo", { n: hours })
  
  const days = Math.floor(hours / 24)
  if (days < 7) return t("time.daysAgo", { n: days })
  
  const locale = language === 'fr' ? 'fr-FR' : 'en-US'
  return date.toLocaleDateString(locale)
}

